// pages/SuppliersPage.tsx
import { useState, useReducer, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Header from '../dashboard/components/Header';
import Sidebar from '../dashboard/components/Sidebar';
import SuppliersList from './components/SuppliersList';
import SupplierForm from './components/SupplierForm';
import SupplierDetail from './components/SupplierDetail';
import SupplierPurchaseHistory from './components/SupplierPurchaseHistory';
import PurchaseOrderForm from '../purchase-orders/components/PurchaseOrderForm';

// Types et reducer inchangés (je les garde tels quels)
type Supplier = {
  id: string;
  reference: string;
  raisonSociale: string;
  nif: string;
  contactPrincipal: string;
  telephone: string;
  email: string;
  adresse: string;
  conditionsPaiement: string;
  devise: string;
  delaiMoyenLivraison: number;
  actif: boolean;
  bloque: boolean;
  motif_blocage?: string | null;
};

type PageState = {
  showForm: boolean;
  showOrderForm: boolean;
  showHistory: boolean;
  showDetail: boolean;
  editingSupplier: Supplier | null;
  selectedSupplier: Supplier | null;
  loading: boolean;
  error: string | null;
};

type PageAction =
  | { type: 'SET_FORM'; payload: { supplier: Supplier | null; show: boolean } }
  | { type: 'SET_ORDER_FORM'; payload: { supplier: Supplier | null; show: boolean } }
  | { type: 'SET_HISTORY'; payload: { supplier: Supplier | null; show: boolean } }
  | { type: 'SET_DETAIL'; payload: { supplier: Supplier | null; show: boolean } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET' };

const pageReducer = (state: PageState, action: PageAction): PageState => {
  switch (action.type) {
    case 'SET_FORM':
      return {
        ...state,
        showForm: action.payload.show,
        editingSupplier: action.payload.supplier,
        showOrderForm: false,
        showHistory: false,
        showDetail: false,
        selectedSupplier: null,
      };
    case 'SET_ORDER_FORM':
      return {
        ...state,
        showOrderForm: action.payload.show,
        selectedSupplier: action.payload.supplier,
        showForm: false,
        showHistory: false,
        showDetail: false,
        editingSupplier: null,
      };
    case 'SET_HISTORY':
      return {
        ...state,
        showHistory: action.payload.show,
        selectedSupplier: action.payload.supplier,
        showForm: false,
        showOrderForm: false,
        showDetail: false,
        editingSupplier: null,
      };
    case 'SET_DETAIL':
      return {
        ...state,
        showDetail: action.payload.show,
        selectedSupplier: action.payload.supplier,
        showForm: false,
        showOrderForm: false,
        showHistory: false,
        editingSupplier: null,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'RESET':
      return {
        showForm: false,
        showOrderForm: false,
        showHistory: false,
        showDetail: false,
        editingSupplier: null,
        selectedSupplier: null,
        loading: false,
        error: null,
      };
    default:
      return state;
  }
};

const initialState: PageState = {
  showForm: false,
  showOrderForm: false,
  showHistory: false,
  showDetail: false,
  editingSupplier: null,
  selectedSupplier: null,
  loading: false,
  error: null,
};

export default function SuppliersPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [state, dispatch] = useReducer(pageReducer, initialState);
  const navigate = useNavigate();

  // Handlers avec useCallback (inchangés)
  const handleAddSupplier = useCallback(() => {
    dispatch({ type: 'SET_FORM', payload: { supplier: null, show: true } });
  }, []);
  const handleEditSupplier = useCallback((supplier: Supplier) => {
    dispatch({ type: 'SET_FORM', payload: { supplier, show: true } });
  }, []);
  const handleCreateOrder = useCallback((supplier: Supplier) => {
    dispatch({ type: 'SET_ORDER_FORM', payload: { supplier, show: true } });
  }, []);
  const handleViewHistory = useCallback((supplier: Supplier) => {
    dispatch({ type: 'SET_HISTORY', payload: { supplier, show: true } });
  }, []);
  const handleViewDetail = useCallback((supplier: Supplier) => {
    dispatch({ type: 'SET_DETAIL', payload: { supplier, show: true } });
  }, []);
  const handleCloseAll = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);
  const handleSuccess = useCallback((message: string) => {
    toast.success(message);
    dispatch({ type: 'RESET' });
  }, []);
  const handleError = useCallback((message: string) => {
    toast.error(message);
    dispatch({ type: 'SET_ERROR', payload: message });
  }, []);

  useEffect(() => {
    if (state.error) {
      const timer = setTimeout(() => dispatch({ type: 'SET_ERROR', payload: null }), 5000);
      return () => clearTimeout(timer);
    }
  }, [state.error]);

  const getCurrentTitle = () => {
    if (state.showForm) return state.editingSupplier ? 'Modifier Fournisseur' : 'Nouveau Fournisseur';
    if (state.showOrderForm) return 'Nouvelle Commande';
    if (state.showHistory) return 'Historique des Achats';
    if (state.showDetail) return 'Détail Fournisseur';
    return 'Fournisseurs';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <Header
        onMenuClick={() => setSidebarOpen(true)}
        loading={state.loading}
        title={getCurrentTitle()}
      />

      {/* Overlay mobile pour fermer sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar responsive */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          className="fixed inset-y-0 left-0 z-50 lg:static lg:translate-x-0 transform transition-transform duration-300 ease-in-out lg:w-64"
        />

        {/* Main content */}
<main className="flex-1 overflow-y-auto pt-16 lg:pt-16 lg:ml-64">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            {/* Erreur globale */}
            {state.error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 mr-2 text-xl" />
                    <span className="text-sm text-red-800">{state.error}</span>
                  </div>
                  <button
                    onClick={() => dispatch({ type: 'SET_ERROR', payload: null })}
                    className="text-red-600 hover:text-red-800"
                  >
                    <i className="ri-close-line text-xl" />
                  </button>
                </div>
              </div>
            )}

             {/* Contenu conditionnel */}
            {state.showForm ? (
              <SupplierForm
                supplier={state.editingSupplier}
                onClose={handleCloseAll}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            ) : state.showOrderForm ? (
              <PurchaseOrderForm
                supplier={state.selectedSupplier!}
                onClose={handleCloseAll}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            ) : state.showDetail ? (
              <SupplierDetail
                supplier={state.selectedSupplier!}
                onClose={handleCloseAll}
                onEdit={handleEditSupplier}
                onCreateOrder={handleCreateOrder}
                onViewHistory={handleViewHistory}
              />
            ) : state.showHistory ? (
              <SupplierPurchaseHistory
                supplierId={state.selectedSupplier!.id}
                supplierName={state.selectedSupplier!.raisonSociale}
                supplierRef={state.selectedSupplier!.reference}
                onClose={handleCloseAll}
              />
            ) : (
              <>
                {/* En-tête de la liste */}
                <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                      title="Retour au tableau de bord"
                      aria-label="Retour"
                    >
                      <i className="ri-arrow-left-line text-xl text-gray-700" />
                    </button>

                    <div className="min-w-0">
                      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                        Fournisseurs
                      </h1>
                      <p className="mt-1 text-sm sm:text-base text-gray-600">
                        Gestion de votre réseau de fournisseurs
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleAddSupplier}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 w-full sm:w-auto transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    <i className="ri-add-line text-lg" />
                    <span>Nouveau Fournisseur</span>
                  </button>
                </div>

                <SuppliersList
                  onEdit={handleEditSupplier}
                  onCreateOrder={handleCreateOrder}
                  onViewHistory={handleViewHistory}
                  onViewDetail={handleViewDetail}
                  loading={state.loading}
                  onError={handleError}
                />
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}